import { KineticModule, Maybe, useLicenseFeaturesQuery } from '@kinetic-ui/data-layer';
import { KINETIC_MODULES } from './modules';
import { isNonNull } from 'kineticUtils/data/isNonNull';
import { getEnvironmentModulesList } from './getEnvironmentModulesList';
import { useMemo } from 'react';

/**
 * Mapping of backend license features, to the UI module packages they should grant access to.
 *
 * If a license does not have an exact corresponding UI module package, the value in this map should be null
 */
const LICENSE_MODULE_TO_UI_MODULE: Record<KineticModule, Maybe<KINETIC_MODULES>> = {
  [KineticModule.C2C]: null,
  [KineticModule.Contacts]: 'contacts',
  [KineticModule.Corridors]: 'corridors',
  [KineticModule.Counts]: 'detectors',
  [KineticModule.Emails]: 'emails',
  [KineticModule.Events]: 'events',
  [KineticModule.Gates]: 'gates',
  [KineticModule.Har]: 'har',
  [KineticModule.RoadAssistance]: 'roadassistance',
  [KineticModule.Integrations]: 'integrations',
  [KineticModule.Fleet]: 'fleet',
  [KineticModule.Parking]: 'parking',
  [KineticModule.Response]: null,
  [KineticModule.Signals]: 'signals',
  [KineticModule.Locations]: null,
  [KineticModule.Signs]: 'signs',
  [KineticModule.System]: null,
  [KineticModule.Tolling]: 'tolling',
  [KineticModule.TravelTime]: 'travel-time',
  [KineticModule.Tunnels]: 'tunnels',
  [KineticModule.Video]: 'video',
  [KineticModule.Weather]: 'weather',
  [KineticModule.ConnectedVehicle]: 'connectedvehicles',
  [KineticModule.AssetManagement]: 'assetmanagement',
  [KineticModule.RoadConditions]: 'road-conditions',
  [KineticModule.RampMeter]: 'ramp-meter',
  [KineticModule.Reports]: null,
  // [KineticModule.Tolling]: 'tolling',
  // [KineticModule.Transit] : 'transit' Todo: Uncomment when permissions is implemented for transit
};

/** Modules that are always enabled and not coupled to license features */
const NON_LICENSED_MODULES: KINETIC_MODULES[] = [
  'communicationlogs',
  'contacts',
  'emails',
  'report-builder',
  'integrations',
  'locations',
];

/** Filters the environment modules by the licenses the user has access to */
export const useLicensedModules = () => {
  const result = useLicenseFeaturesQuery({ fetchPolicy: 'cache-first' });
  const envModules = getEnvironmentModulesList();

  const licensedModules = useMemo(() => {
    if (!result.data?.licenseFeatures) {
      return [];
    }
    return result.data.licenseFeatures
      .filter(({ enabled }) => enabled)
      .map(({ feature }) => LICENSE_MODULE_TO_UI_MODULE[feature])
      .filter(isNonNull);
  }, [result.data?.licenseFeatures]);

  const allowedModules = useMemo(
    () =>
      envModules.filter(
        (envModule) =>
          NON_LICENSED_MODULES.includes(envModule) || licensedModules.includes(envModule)
      ),
    [envModules, licensedModules]
  );

  return allowedModules;
};
